import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, MessageSquarePlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { captureScreenshot } from '@/lib/feedback/captureScreenshot'
import { startReplayBuffering } from '@/lib/feedback/replay'
import { FeedbackDialog } from './FeedbackDialog'

interface FeedbackButtonProps {
  /** 'icon' for the sidebar, 'button' for page headers. */
  variant?: 'icon' | 'button'
}

export function FeedbackButton({ variant = 'icon' }: FeedbackButtonProps) {
  const { t } = useTranslation('feedback')
  const [capturing, setCapturing] = useState(false)
  const [open, setOpen] = useState(false)
  const [screenshot, setScreenshot] = useState<string | null>(null)
  // A new key per opening gives the dialog a fresh form for the new screenshot.
  const [session, setSession] = useState(0)

  useEffect(() => startReplayBuffering(), [])

  const handleClick = async () => {
    if (capturing) return
    setCapturing(true)
    try {
      setScreenshot(await captureScreenshot())
    } finally {
      setCapturing(false)
    }
    setSession((current) => current + 1)
    setOpen(true)
  }

  const Icon = capturing ? Loader2 : MessageSquarePlus
  const iconClass = capturing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'

  return (
    <>
      {variant === 'icon' ? (
        <button
          type="button"
          data-feedback-ignore=""
          onClick={handleClick}
          aria-busy={capturing}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title={t('button.label')}
          aria-label={t('button.label')}
        >
          <Icon className={iconClass} />
        </button>
      ) : (
        <Button
          type="button"
          data-feedback-ignore=""
          onClick={handleClick}
          aria-busy={capturing}
          aria-label={t('button.label')}
        >
          <Icon className={`mr-2 ${iconClass}`} />
          {t('button.short')}
        </Button>
      )}
      <FeedbackDialog key={session} open={open} onOpenChange={setOpen} screenshot={screenshot} />
    </>
  )
}
